import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Link, useLocation } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  ShieldCheck, ArrowLeft, Trash2, CheckCircle2, Loader2, MessageSquare, FileText,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const SUPPORT_EMAIL = 'suporte@bunker.app.br';
const COMPANY_NAME = 'Bunker — Marketplace Growth OS';
const LAST_UPDATED = '6 de setembro de 2026';

const LegalShell = ({ title, icon: Icon, children }) => (
  <div className="min-h-screen bg-slate-50">
    <header className="border-b border-slate-200 bg-white/80 backdrop-blur sticky top-0 z-10">
      <div className="max-w-3xl mx-auto px-6 py-3 flex items-center justify-between">
        <Link to="/login" className="text-sm text-slate-500 hover:text-slate-900 flex items-center gap-1.5" data-testid="legal-back-link">
          <ArrowLeft className="w-4 h-4" /> Voltar
        </Link>
        <span className="text-xs font-bold text-slate-400 tracking-wider">{COMPANY_NAME.toUpperCase()}</span>
      </div>
    </header>
    <main className="max-w-3xl mx-auto px-6 py-10">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-12 h-12 rounded-xl bg-emerald-100 text-emerald-600 flex items-center justify-center shadow">
          <Icon className="w-6 h-6" />
        </div>
        <h1 className="text-3xl font-bold text-slate-900">{title}</h1>
      </div>
      <p className="text-xs text-slate-400 mb-8">Última atualização: {LAST_UPDATED}</p>
      <div className="legal-content space-y-4 text-sm text-slate-700 leading-relaxed [&_h2]:text-xl [&_h2]:font-bold [&_h2]:text-slate-900 [&_h2]:mt-8 [&_h2]:mb-2 [&_p]:my-3 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:my-3 [&_li]:my-1 [&_a]:text-emerald-700 [&_a]:underline [&_strong]:text-slate-900 [&_code]:bg-slate-100 [&_code]:px-1 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-[12px] [&_code]:font-mono">
        {children}
      </div>
      <footer className="mt-12 pt-6 border-t border-slate-200 text-xs text-slate-400 flex items-center justify-between flex-wrap gap-2">
        <span>© {new Date().getFullYear()} {COMPANY_NAME}</span>
        <div className="flex items-center gap-3">
          <Link to="/privacy-policy" className="hover:text-slate-700" data-testid="legal-link-privacy">Política de Privacidade</Link>
          <Link to="/terms-of-use" className="hover:text-slate-700" data-testid="legal-link-terms">Termos de Uso</Link>
          <Link to="/data-deletion" className="hover:text-slate-700" data-testid="legal-link-deletion">Exclusão de Dados</Link>
        </div>
      </footer>
    </main>
  </div>
);

// ─── Terms of Use ──────────────────────────────────────────────────────
export const TermsOfUsePage = () => (
  <LegalShell title="Termos de Uso" icon={FileText}>
    <p>
      Estes Termos regem o uso do <strong>{COMPANY_NAME}</strong>, plataforma de apoio à
      operação de comércio eletrônico, criação de conteúdo e integração com marketplaces.
      Ao utilizar o Bunker, você declara ter capacidade e autorização para representar as
      contas, lojas e empresas que conectar.
    </p>

    <h2>1. Finalidade do serviço</h2>
    <p>
      O Bunker organiza dados operacionais, auxilia na criação e revisão de anúncios,
      sincroniza informações autorizadas e disponibiliza automações configuráveis. As
      funcionalidades efetivamente disponíveis dependem das APIs, permissões e regras de
      cada marketplace.
    </p>

    <h2>2. Conta e credenciais</h2>
    <ul>
      <li>Você é responsável pela exatidão dos dados da conta e pela segurança dos seus acessos.</li>
      <li>Credenciais de terceiros devem ser inseridas somente por pessoa autorizada.</li>
      <li>Tokens e segredos integrados são criptografados e não são exibidos novamente após o salvamento.</li>
      <li>Você pode revogar uma integração a qualquer momento no Bunker e no provedor correspondente.</li>
    </ul>

    <h2>3. Integrações com marketplaces</h2>
    <p>
      Ao conectar Mercado Livre, Magalu, Shopee ou outro canal, você autoriza o Bunker a
      acessar os dados e executar apenas as ações abrangidas pelos escopos aprovados. O uso
      também permanece sujeito aos termos, políticas, limites e disponibilidade técnica do
      respectivo marketplace.
    </p>

    <h2>4. Inteligência artificial</h2>
    <ul>
      <li>Resultados de IA são sugestões e podem conter erros ou informações que exigem confirmação.</li>
      <li>Dados não confirmados não devem ser apresentados como fatos do produto.</li>
      <li>Você deve revisar títulos, descrições, atributos, imagens, preços e condições antes da publicação.</li>
      <li>O Bunker não garante vendas, posicionamento, aprovação de anúncio ou resultado financeiro.</li>
    </ul>

    <h2>5. Uso permitido</h2>
    <p>É proibido utilizar a plataforma para fraude, avaliações ou vendas artificiais, violação de propriedade intelectual, evasão de controles de acesso, publicação de produtos proibidos ou descumprimento das políticas dos marketplaces.</p>

    <h2>6. Publicação e automações</h2>
    <p>
      Ações externas obedecem às permissões configuradas. Modos de simulação, aprovação,
      limites e bloqueios operacionais devem ser revisados antes de habilitar execução
      automática. Você continua responsável por preços, estoque, tributos, conteúdo e
      obrigações decorrentes das publicações realizadas em suas contas.
    </p>

    <h2>7. Disponibilidade e alterações</h2>
    <p>
      Podemos corrigir, atualizar ou suspender recursos para manutenção, segurança ou
      adequação a mudanças das APIs. Integrações externas podem ficar indisponíveis sem
      controle do Bunker. Alterações relevantes destes Termos serão comunicadas na plataforma.
    </p>

    <h2>8. Privacidade e exclusão</h2>
    <p>
      O tratamento de dados segue nossa <Link to="/privacy-policy">Política de Privacidade</Link>.
      Solicitações de encerramento e exclusão podem ser feitas em <Link to="/data-deletion">Exclusão de Dados</Link>.
    </p>

    <h2>9. Contato</h2>
    <p>
      Dúvidas sobre estes Termos podem ser enviadas para{' '}
      <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
    </p>
  </LegalShell>
);

// ─── Privacy Policy ─────────────────────────────────────────────────────
export const PrivacyPolicyPage = () => (
  <LegalShell title="Política de Privacidade" icon={ShieldCheck}>
    <p>
      Esta Política de Privacidade descreve como o <strong>{COMPANY_NAME}</strong>
      (“nós”, “Bunker”, “plataforma”) coleta, usa, armazena e protege as informações dos
      usuários (“você”, “lojista”) que utilizam nossa plataforma e a integração com
      WhatsApp Business via Meta Cloud API.
    </p>

    <h2>1. Quais dados coletamos</h2>
    <ul>
      <li><strong>Dados da conta Bunker:</strong> nome, e-mail, senha (armazenada com hash) e dados de login.</li>
      <li><strong>Credenciais de marketplaces e WhatsApp:</strong> tokens de acesso, App ID, App Secret, Verify Token e
      Phone Number ID. Todos são <strong>criptografados em repouso</strong> (Fernet + PBKDF2HMAC) e nunca aparecem
      em texto puro depois de salvos.</li>
      <li><strong>Mensagens recebidas via webhook do WhatsApp:</strong> conteúdo de mensagens de texto, áudio, imagem,
      vídeo, documentos e identificadores (phone_number_id, message_id, contato).</li>
      <li><strong>Dados operacionais:</strong> anúncios, drafts, pedidos, métricas de marketplace, histórico de
      execuções de IA, aprendizados (regras estratégicas) e personalidade do gerente do WhatsApp.</li>
      <li><strong>Logs técnicos:</strong> latência, status de chamadas a APIs externas, erros e auditoria de ações.</li>
    </ul>

    <h2>2. Como usamos seus dados</h2>
    <ul>
      <li>Operar a plataforma: criar e publicar anúncios, sincronizar marketplaces, gerar imagens e textos por IA.</li>
      <li>Responder mensagens recebidas no seu WhatsApp Business e executar comandos solicitados por você.</li>
      <li>Aprender suas preferências (estilo de comunicação, foco do negócio, regras estratégicas) para personalizar
      respostas — sempre dentro da sua própria conta.</li>
      <li>Detectar abusos, fraudes e problemas operacionais por meio de logs e métricas de auditoria.</li>
    </ul>

    <h2>3. Conexão com WhatsApp Business (Meta Cloud API)</h2>
    <p>
      Quando você conecta seu número via Meta Cloud API, o Bunker:
    </p>
    <ul>
      <li>Recebe mensagens enviadas para o número conectado por meio de webhook autenticado por <strong>Verify Token</strong> único.</li>
      <li>Envia mensagens em nome da sua conta de WhatsApp Business usando o seu próprio Access Token (System User).</li>
      <li>Não compartilha o conteúdo das mensagens com terceiros, exceto com os provedores de IA que <strong>você
      configurou</strong> em "Conectar Minha IA" (OpenAI, Anthropic, Google Gemini, Ollama, etc.).</li>
      <li>Não usa sua chave de IA padrão do Bunker para processar mensagens do seu WhatsApp — usamos apenas a chave
      configurada por você.</li>
    </ul>

    <h2>4. Compartilhamento com terceiros</h2>
    <p>
      Compartilhamos dados estritamente necessários com os seguintes provedores que <strong>você configura</strong>:
    </p>
    <ul>
      <li><strong>Meta Platforms Inc.</strong> — para envio e recebimento de mensagens via WhatsApp Cloud API.</li>
      <li><strong>Mercado Livre, Shopee</strong> e outros marketplaces — para publicar e sincronizar seus anúncios.</li>
      <li><strong>Provedores de IA</strong> (OpenAI, Anthropic, Google, Ollama, etc.) — apenas o conteúdo necessário
      para a tarefa solicitada (ex: gerar título, gerar descrição, gerar imagem, responder mensagem).</li>
    </ul>
    <p>Não vendemos, alugamos ou comercializamos seus dados pessoais com fins publicitários.</p>

    <h2>5. Segurança</h2>
    <ul>
      <li>Tokens, chaves e segredos são criptografados em repouso com Fernet + PBKDF2HMAC.</li>
      <li>O Verify Token é exibido em texto puro <strong>apenas uma vez</strong>, no momento da criação da conexão.</li>
      <li>Comunicação cliente↔servidor sempre via HTTPS/TLS.</li>
      <li>Nenhum endpoint da plataforma retorna chaves criptografadas para o navegador — apenas hints
      mascarados (ex: <code>sk-****abcd</code>).</li>
      <li>Logs nunca persistem chaves de API em texto puro.</li>
      <li>Acesso multi-tenant rigoroso: cada usuário só enxerga seus próprios dados (filtro <code>owner_id</code>
      em todas as consultas).</li>
    </ul>

    <h2>6. Retenção</h2>
    <ul>
      <li>Mensagens de WhatsApp recebidas: armazenadas enquanto a conexão existir, ou até que você solicite a
      exclusão.</li>
      <li>Histórico de execuções de IA (<code>ai_task_runs</code>): retenção automática de <strong>90 dias</strong>
      (TTL no MongoDB).</li>
      <li>Logs de auditoria de chamadas externas: retenção de <strong>30 dias</strong>.</li>
      <li>Drafts e anúncios: enquanto sua conta estiver ativa.</li>
    </ul>

    <h2>7. Seus direitos (LGPD/GDPR)</h2>
    <p>Você pode, a qualquer momento:</p>
    <ul>
      <li>Acessar uma cópia dos seus dados (escreva para <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>).</li>
      <li>Corrigir dados imprecisos diretamente na plataforma.</li>
      <li>Excluir todos os seus dados utilizando a página{' '}
      <Link to="/data-deletion" className="text-emerald-700 underline">Exclusão de Dados</Link>.</li>
      <li>Revogar tokens de marketplaces e WhatsApp a qualquer momento removendo a conexão correspondente.</li>
      <li>Exportar seus dados em formato JSON (mediante solicitação por e-mail).</li>
    </ul>

    <h2>8. Cookies e armazenamento local</h2>
    <p>
      Usamos <code>localStorage</code> apenas para guardar o token de autenticação da sua sessão Bunker. Não
      utilizamos cookies de rastreamento publicitário.
    </p>

    <h2>9. Menores de idade</h2>
    <p>
      A plataforma é destinada a maiores de 18 anos. Não coletamos conscientemente dados de menores.
    </p>

    <h2>10. Alterações nesta política</h2>
    <p>
      Podemos atualizar esta Política periodicamente. Mudanças relevantes serão comunicadas por e-mail e/ou notificação
      dentro da plataforma. A data da última revisão está indicada no topo desta página.
    </p>

    <h2>11. Contato</h2>
    <p>
      Para dúvidas sobre privacidade, dados pessoais ou esta Política, entre em contato em{' '}
      <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
    </p>
  </LegalShell>
);

// ─── Data Deletion ──────────────────────────────────────────────────────
export const DataDeletionPage = () => {
  const location = useLocation();
  const [form, setForm] = useState({
    email: '',
    phone_number: '',
    reason: '',
    confirm: false,
  });
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(null); // {request_id, expected_completion_at}
  const [lookupStatus, setLookupStatus] = useState(null); // {request_id, status, ...}
  const [lookupError, setLookupError] = useState(null);

  // When Meta's callback redirects users here with ?req=FBDEL-XXX, fetch & show status
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const reqId = params.get('req');
    if (!reqId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/whatsapp/data-deletion-status/${encodeURIComponent(reqId)}`);
        if (!cancelled) setLookupStatus(data);
      } catch (err) {
        if (!cancelled) setLookupError(err.response?.data?.detail || 'Pedido não encontrado.');
      }
    })();
    return () => { cancelled = true; };
  }, [location.search]);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const onSubmit = async () => {
    if (!form.email.trim() && !form.phone_number.trim()) {
      toast.error('Informe e-mail OU número de WhatsApp para identificar a conta.');
      return;
    }
    if (!form.confirm) {
      toast.error('Confirme que você entende que a exclusão é definitiva.');
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/whatsapp/data-deletion-request`, {
        email: form.email.trim() || undefined,
        phone_number: form.phone_number.trim() || undefined,
        reason: form.reason.trim() || undefined,
      });
      setSubmitted({
        request_id: data.request_id,
        expected_completion_at: data.expected_completion_at,
      });
      toast.success('Pedido de exclusão registrado.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Não foi possível registrar o pedido.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <LegalShell title="Exclusão de Dados do Usuário" icon={Trash2}>
      {(lookupStatus || lookupError) && (
        <Card className={`not-prose mb-6 ${lookupStatus ? 'border-emerald-300 bg-emerald-50' : 'border-amber-300 bg-amber-50'}`} data-testid="data-deletion-lookup-card">
          <CardContent className="p-5">
            <div className="flex items-start gap-3">
              {lookupStatus
                ? <CheckCircle2 className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-0.5" />
                : <Trash2 className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />}
              <div className="flex-1 min-w-0">
                {lookupStatus ? (
                  <>
                    <p className="font-bold text-emerald-900 mb-1">Pedido de exclusão encontrado</p>
                    <p className="text-sm text-emerald-800">
                      Código: <code className="bg-white px-2 py-0.5 rounded border border-emerald-200 font-mono text-xs">{lookupStatus.request_id}</code>
                    </p>
                    <p className="text-sm text-emerald-800 mt-1">
                      Status atual: <strong className="capitalize">{lookupStatus.status}</strong>
                    </p>
                    <p className="text-sm text-emerald-800 mt-1">
                      Conclusão prevista até: <strong>{new Date(lookupStatus.expected_completion_at).toLocaleDateString('pt-BR')}</strong>
                    </p>
                    {lookupStatus.processed_at && (
                      <p className="text-sm text-emerald-800 mt-1">
                        Processado em: <strong>{new Date(lookupStatus.processed_at).toLocaleDateString('pt-BR')}</strong>
                      </p>
                    )}
                    <p className="text-xs text-emerald-700 mt-3">
                      Em caso de dúvidas, escreva para{' '}
                      <a href={`mailto:${SUPPORT_EMAIL}`} className="underline">{SUPPORT_EMAIL}</a> citando o código.
                    </p>
                  </>
                ) : (
                  <>
                    <p className="font-bold text-amber-900 mb-1">Pedido não encontrado</p>
                    <p className="text-sm text-amber-800">{lookupError}</p>
                    <p className="text-xs text-amber-700 mt-2">
                      Você pode preencher o formulário abaixo para registrar uma nova solicitação.
                    </p>
                  </>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      <p>
        Você tem o direito de solicitar a exclusão de todos os dados pessoais armazenados pelo
        <strong> {COMPANY_NAME}</strong> a qualquer momento — incluindo dados coletados via integração com
        WhatsApp Business (Meta Cloud API).
      </p>

      <h2>O que será excluído</h2>
      <ul>
        <li>Sua conta Bunker e credenciais de login.</li>
        <li>Todas as conexões de WhatsApp configuradas (tokens, Verify Tokens, Phone Number IDs).</li>
        <li>Todas as mensagens de WhatsApp recebidas e enviadas armazenadas pelo Bunker.</li>
        <li>Personalidade configurada do gerente, aprendizados (regras) e histórico de sessões.</li>
        <li>Todos os anúncios, drafts, fotos geradas e métricas associadas à sua conta.</li>
        <li>Conexões com marketplaces (Mercado Livre, Shopee) e provedores de IA conectados.</li>
        <li>Logs de auditoria associados ao seu <code>owner_id</code>.</li>
      </ul>

      <h2>O que NÃO podemos excluir imediatamente</h2>
      <ul>
        <li>Backups criptografados retidos por até <strong>30 dias</strong> (sobrescritos automaticamente).</li>
        <li>Registros que precisamos manter por obrigação legal/fiscal (notas fiscais, faturamento) — anonimizados
        após 5 anos conforme legislação brasileira.</li>
        <li>Dados que estão sob controle de terceiros que você autorizou (Meta, marketplaces, provedores de IA) —
        para esses, é necessário também solicitar exclusão diretamente em cada provedor.</li>
      </ul>

      <h2>Prazo</h2>
      <p>
        O Bunker processa pedidos de exclusão em <strong>até 30 dias</strong> após o registro do pedido. Você
        receberá um e-mail de confirmação quando a exclusão for concluída.
      </p>

      <h2>Como solicitar</h2>
      <p>Preencha o formulário abaixo. Você pode usar e-mail, número de WhatsApp ou ambos para localizarmos sua conta.</p>

      {submitted ? (
        <Card className="border-emerald-300 bg-emerald-50 not-prose mt-4">
          <CardContent className="p-5">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-bold text-emerald-900 mb-1">Pedido registrado com sucesso</p>
                <p className="text-sm text-emerald-800">
                  Código do pedido: <code className="bg-white px-2 py-0.5 rounded border border-emerald-200 font-mono text-xs" data-testid="data-deletion-request-id">{submitted.request_id}</code>
                </p>
                <p className="text-sm text-emerald-800 mt-1">
                  Conclusão prevista até: <strong>{new Date(submitted.expected_completion_at).toLocaleDateString('pt-BR')}</strong>
                </p>
                <p className="text-xs text-emerald-700 mt-3">
                  Guarde esse código. Em caso de dúvidas, escreva para{' '}
                  <a href={`mailto:${SUPPORT_EMAIL}`} className="underline">{SUPPORT_EMAIL}</a> citando-o.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-slate-200 not-prose mt-4">
          <CardContent className="p-5 space-y-4">
            <div>
              <Label className="text-xs">E-mail da conta Bunker <span className="text-slate-400">(recomendado)</span></Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => set('email', e.target.value)}
                placeholder="seu@email.com"
                data-testid="data-deletion-email"
                autoComplete="email"
              />
            </div>
            <div>
              <Label className="text-xs flex items-center gap-1">
                <MessageSquare className="w-3 h-3" /> Número de WhatsApp conectado <span className="text-slate-400">(opcional)</span>
              </Label>
              <Input
                type="tel"
                value={form.phone_number}
                onChange={(e) => set('phone_number', e.target.value)}
                placeholder="+55 11 99999-0000"
                data-testid="data-deletion-phone"
              />
              <p className="text-[10px] text-slate-400 mt-0.5">
                Use o número que você registrou em WhatsApp Business / Phone Number ID.
              </p>
            </div>
            <div>
              <Label className="text-xs">Motivo <span className="text-slate-400">(opcional, ajuda a melhorar)</span></Label>
              <Textarea
                rows={3}
                value={form.reason}
                onChange={(e) => set('reason', e.target.value)}
                placeholder="Conte brevemente por que está saindo..."
                data-testid="data-deletion-reason"
              />
            </div>
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.confirm}
                onChange={(e) => set('confirm', e.target.checked)}
                data-testid="data-deletion-confirm"
                className="mt-0.5"
              />
              <span className="text-xs text-slate-700">
                Entendo que esta ação é <strong>definitiva e irreversível</strong>. Após a exclusão, não será
                possível recuperar minha conta, anúncios ou mensagens.
              </span>
            </label>
            <Button
              onClick={onSubmit}
              disabled={busy || !form.confirm}
              className="w-full bg-red-600 hover:bg-red-700 text-white"
              data-testid="data-deletion-submit"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Solicitar exclusão de dados
            </Button>
            <p className="text-[11px] text-slate-400 text-center">
              Você receberá uma confirmação por e-mail. Em caso de dúvidas, escreva para{' '}
              <a href={`mailto:${SUPPORT_EMAIL}`} className="underline">{SUPPORT_EMAIL}</a>.
            </p>
          </CardContent>
        </Card>
      )}

      <h2>Alternativa: revogar acesso a serviços específicos</h2>
      <p>Se você quer apenas desconectar um serviço sem excluir sua conta:</p>
      <ul>
        <li><strong>WhatsApp:</strong> em <em>WhatsApp Manager → Conexão</em>, clique em "Remover" no card da conexão.</li>
        <li><strong>Mercado Livre:</strong> em <em>Integrações</em>, clique em "Desconectar Mercado Livre".</li>
        <li><strong>IAs conectadas:</strong> em <em>Conectar Minha IA</em>, remova cada integração individualmente.</li>
      </ul>
    </LegalShell>
  );
};

export default { PrivacyPolicyPage, TermsOfUsePage, DataDeletionPage };
